import { useMemo } from 'react';

import { useFieldArray, useFormContext } from 'react-hook-form';
import { FaPlus } from 'react-icons/fa6';
import { HiTrash } from 'react-icons/hi';
import { WithContext as ReactTags, SEPARATORS } from 'react-tag-input';

import classNames from 'classnames';

import { Button } from 'components/Button';

import {
    CURRENCIES,
    MOVE_MODES,
    PICKUP_DATES,
    VEHICLE_CLASSES,
} from './consts';

import styles from 'styles/Form.module.css';

const QuoteRestrictionCriteria = ({
    name,
    available_items,
    selected_items = [],
    onChange,
}) => {
    const tags = useMemo(() => {
        return selected_items.map((item) => ({
            id: item,
            text: available_items.find((i) => i.value === item).label,
            className: '',
        }));
    }, [available_items, selected_items]);

    const suggestions = available_items.map((item) => {
        return {
            id: item.value,
            text: item.label,
            className: '',
        };
    });

    const handleDelete = (index) => {
        onChange?.(selected_items.filter((_, i) => i !== index));
    };

    const handleAddition = (tag) => {
        if (available_items.find((i) => i.value === tag.id)) {
            onChange?.([...selected_items, tag.id]);
        }
    };

    const handleInputBlur = (value) => {
        if (value) {
            handleAddition({ id: value, className: '' });
        }
    };

    return (
        <div
            style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                flexShrink: 0,
            }}
        >
            <span
                style={{
                    color: 'var(--secondary-darkest)',
                    fontSize: 'var(--text-xs)',
                    fontWeight: 600,
                }}
            >
                {name}
            </span>
            <ReactTags
                tags={tags}
                suggestions={suggestions}
                separators={[
                    SEPARATORS.ENTER,
                    SEPARATORS.COMMA,
                    SEPARATORS.SPACE,
                    SEPARATORS.TAB,
                ]}
                handleAddition={handleAddition}
                handleDelete={handleDelete}
                handleInputBlur={handleInputBlur}
                placeholder='+'
                classNames={{
                    tagInput: classNames(
                        styles.tagsTagInput,
                        styles.quoteRestrictionsTagInput
                    ),
                    tagInputField: classNames(
                        styles.tagsTagInputField,
                        styles.quoteRestrictionsTagInputField
                    ),
                    selected: classNames(
                        styles.tagsSelected,
                        styles.quoteRestrictionsSelected
                    ),
                    tag: classNames(
                        styles.tagsTag,
                        styles.quoteRestrictionsTag
                    ),
                    remove: classNames(
                        styles.tagsRemove,
                        'ReactTags__remove' // Bug in the library that uses the original classname
                    ),
                    suggestions: styles.tagsSuggestions,
                    activeSuggestion: styles.tagsActiveSuggestion,
                }}
                minQueryLength={-1}
            />
        </div>
    );
};

const QuoteRestrictionItem = ({ name_prefix, index, remove }) => {
    const { watch, setValue } = useFormContext();

    return (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
            <QuoteRestrictionCriteria
                name='Vehicle class'
                available_items={VEHICLE_CLASSES}
                selected_items={watch(
                    `${name_prefix}.${index}.vehicle_classes`
                )}
                onChange={(vehicles) =>
                    setValue(
                        `${name_prefix}.${index}.vehicle_classes`,
                        vehicles
                    )
                }
            />

            <QuoteRestrictionCriteria
                name='Move mode'
                available_items={MOVE_MODES}
                selected_items={watch(`${name_prefix}.${index}.move_modes`)}
                onChange={(move_modes) =>
                    setValue(`${name_prefix}.${index}.move_modes`, move_modes)
                }
            />

            <QuoteRestrictionCriteria
                name='Pickup date'
                available_items={PICKUP_DATES.map((item) => ({
                    ...item,
                    value: String(item.value),
                }))}
                selected_items={watch(`${name_prefix}.${index}.pickup_dates`)}
                onChange={(pickup_dates) =>
                    setValue(
                        `${name_prefix}.${index}.pickup_dates`,
                        pickup_dates
                    )
                }
            />

            <QuoteRestrictionCriteria
                name='Currency'
                available_items={CURRENCIES}
                selected_items={watch(`${name_prefix}.${index}.currencies`)}
                onChange={(currencies) =>
                    setValue(`${name_prefix}.${index}.currencies`, currencies)
                }
            />

            <Button
                uitype='icon'
                radius='round'
                color='secondary-dark'
                onClick={(event) => {
                    event.preventDefault();
                    remove(index);
                }}
            >
                <HiTrash title='Delete restriction' />
            </Button>
        </div>
    );
};

const QuoteRestrictions = ({ name_prefix }) => {
    const { control, watch } = useFormContext();

    const { fields, append, remove } = useFieldArray({
        control,
        name: name_prefix,
    });

    const getLabelFromValue = (options, value) => {
        return options.find((option) => option.value === value)?.label;
    };

    const restriction_rules = watch(name_prefix)?.reduce((acc, item) => {
        for (const vehicle_class of item.vehicle_classes?.length
            ? item.vehicle_classes
            : [null]) {
            for (const move_mode of item.move_modes?.length
                ? item.move_modes
                : [null]) {
                for (const pickup_date of item.pickup_dates?.length
                    ? item.pickup_dates
                    : [null]) {
                    for (const currency of item.currencies?.length
                        ? item.currencies
                        : [null]) {
                        const criteria = [];
                        if (vehicle_class) {
                            criteria.push(
                                `Vehicle Class is&nbsp;<b>${getLabelFromValue(
                                    VEHICLE_CLASSES,
                                    vehicle_class
                                )}</b>&nbsp;`
                            );
                        }
                        if (move_mode) {
                            criteria.push(
                                `Move Mode is&nbsp;<b>${getLabelFromValue(
                                    MOVE_MODES,
                                    move_mode
                                )}</b>&nbsp;`
                            );
                        }
                        if (pickup_date) {
                            criteria.push(
                                `Pickup Date is&nbsp;<b>${getLabelFromValue(
                                    PICKUP_DATES,
                                    Number(pickup_date)
                                )}</b>&nbsp;`
                            );
                        }
                        if (currency) {
                            criteria.push(
                                `Currency is&nbsp;<b>${getLabelFromValue(
                                    CURRENCIES,
                                    currency
                                )}</b>&nbsp;`
                            );
                        }

                        if (criteria.length) {
                            acc.push(
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: criteria.join('AND '),
                                    }}
                                />
                            );
                        }
                    }
                }
            }
        }
        return acc;
    }, []);

    return (
        <div>
            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '12px',
                }}
            >
                {fields.map((item, index) => (
                    <QuoteRestrictionItem
                        key={item.id}
                        name_prefix={name_prefix}
                        index={index}
                        remove={remove}
                    />
                ))}
            </div>

            <Button
                uitype='ghost'
                color='primary-dark'
                size='xs'
                onClick={(event) => {
                    event.preventDefault();
                    append({});
                }}
                style={{ marginTop: '8px' }}
            >
                <FaPlus /> Add restriction
            </Button>

            {Boolean(restriction_rules?.length) && (
                <div>
                    <h4 style={{ marginBottom: 0 }}>
                        A lane will not be quoted if any of the following
                        conditions are met:
                    </h4>
                    <ol
                        style={{
                            lineHeight: '1.75rem',
                            paddingLeft: '2rem',
                        }}
                    >
                        {restriction_rules.map((rule) => (
                            <li style={{}}>{rule}</li>
                        ))}
                    </ol>
                </div>
            )}
        </div>
    );
};

export { QuoteRestrictions };
